import { makeAutoObservable } from 'mobx'

const toStr = v => (v == null ? '' : String(v))
const normalizeId = v => toStr(v).trim()

const startOfDay = d =>
	new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)

const isSameDay = (a, b) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate()

const inSameMonth = (a, b) =>
	a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

const parseDateSafe = value => {
	if (!value) return null

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value
	}

	if (typeof value === 'number') {
		const dt = new Date(value)
		return Number.isNaN(dt.getTime()) ? null : dt
	}

	const s = String(value).trim()
	if (!s) return null

	const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
	if (isoDay) {
		const y = Number(isoDay[1])
		const m = Number(isoDay[2]) - 1
		const d = Number(isoDay[3])
		const local = new Date(y, m, d, 0, 0, 0, 0)
		return Number.isNaN(local.getTime()) ? null : local
	}

	const dt = new Date(s)
	return Number.isNaN(dt.getTime()) ? null : dt
}

const getDueDate = row =>
	parseDateSafe(row?.dueDate ?? row?.due ?? row?.deadline ?? row?.date ?? null)

/** Types shown under the aggregate "Applications" tab (essay, documents, LoR — no separate LoR tab). */
const isApplicationsAggregateType = raw => {
	const t = String(raw || '')
		.toLowerCase()
		.replace(/\s+/g, '')
	if (!t) return false
	if (t === 'essay' || t === 'document' || t === 'application') return true
	if (t === 'lor') return true
	if (t === 'letterofrecommendation' || t === 'recommendation') return true
	return false
}

class DashboardStore {
	activeGrade = 'all'

	activeTableTab = 'tasks'
	activeQuickFilter = 'all'

	sortKey = null
	sortDir = 'asc'

	rows = []

	tasksFetchedOnce = false
	tasksLastResponseWasEmpty = false
	kpisFetchedOnce = false
	kpiStatsCache = null

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setTasksFetchedOnce(value) {
		this.tasksFetchedOnce = value
	}

	setTasksLastResponseWasEmpty(value) {
		this.tasksLastResponseWasEmpty = value
	}

	setKpisFetchedOnce(value) {
		this.kpisFetchedOnce = value
	}

	setKpiStatsCache(data) {
		this.kpiStatsCache = data
	}

	setGrabe(grab) {
		this.activeGrade = grab
	}

	setRows(rows) {
		if (!Array.isArray(rows)) {
			this.rows = []
			return
		}
		this.rows = rows.map(r => {
			const id = normalizeId(r?.id ?? r?.task_id ?? '')
			const task = r?.task ?? r?.task_title ?? r?.title ?? ''
			const type = r?.type ?? ''
			const studentName = r?.studentName ?? r?.student_name ?? ''
			const studentId = normalizeId(r?.studentId ?? r?.student_id ?? '')
			const studentCountry = r?.studentCountry ?? r?.student_country ?? ''
			const dueDate = r?.dueDate ?? r?.due_date ?? r?.due ?? null
			return {
				...r,
				id: id || `row-${Math.random().toString(36).slice(2)}`,
				task,
				type,
				studentName,
				studentId: studentId || r?.studentId || r?.student_id || '',
				studentCountry,
				dueDate: dueDate ?? r?.due,
			}
		})
	}

	setActiveTableTab(tab) {
		this.activeTableTab = tab || 'tasks'
	}

	setActiveQuickFilter(filter) {
		this.activeQuickFilter = filter || 'all'
	}

	setSort(key, dir) {
		if (!key) return
		if (dir === 'asc' || dir === 'desc') {
			this.sortKey = key
			this.sortDir = dir
			return
		}
		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
		} else {
			this.sortKey = key
			this.sortDir = 'asc'
		}
	}

	clearSort() {
		this.sortKey = null
		this.sortDir = 'asc'
	}

	get tableTabs() {
		return [
			{ id: 'tasks', label: 'Tasks' },
			{ id: 'applications', label: 'Applications' },
			{ id: 'documents', label: 'Documents' },
			{ id: 'essay', label: 'Essay' },
		]
	}

	get quickFilters() {
		return [
			{ id: 'all', label: 'All' },
			{ id: 'today', label: 'Today' },
			{ id: 'thisMonth', label: 'This Month' },
			{ id: 'academic', label: 'This Academic session' },
		]
	}

	get tableRows() {
		const now = new Date()
		const today = startOfDay(now)

		let res = Array.isArray(this.rows) ? [...this.rows] : []

		res = res.filter(r => {
			const t = r?.type || ''
			if (this.activeTableTab === 'tasks') return true
			if (this.activeTableTab === 'applications')
				return isApplicationsAggregateType(t)
			if (this.activeTableTab === 'documents')
				return String(t).toLowerCase() === 'document'
			if (this.activeTableTab === 'essay') return String(t).toLowerCase() === 'essay'
			return true
		})

		if (this.activeQuickFilter !== 'all') {
			res = res.filter(r => {
				const d = getDueDate(r)
				if (!d) return false

				if (this.activeQuickFilter === 'today') return isSameDay(d, today)
				if (this.activeQuickFilter === 'thisMonth') return inSameMonth(d, today)

				if (this.activeQuickFilter === 'academic') {
					const y = today.getFullYear()
					const m = today.getMonth()
					const startYear = m >= 8 ? y : y - 1
					const start = new Date(startYear, 8, 1, 0, 0, 0, 0)
					const end = new Date(startYear + 1, 7, 31, 23, 59, 59, 999)
					return d >= start && d <= end
				}

				return true
			})
		}

		if (this.sortKey) {
			const dir = this.sortDir === 'desc' ? -1 : 1
			const key = this.sortKey

			res.sort((a, b) => {
				if (key === 'due') {
					const ad = getDueDate(a)
					const bd = getDueDate(b)
					const av = ad ? ad.getTime() : Number.POSITIVE_INFINITY
					const bv = bd ? bd.getTime() : Number.POSITIVE_INFINITY
					return (av - bv) * dir
				}

				const av = a?.[key]
				const bv = b?.[key]
				if (av == null && bv == null) return 0
				if (av == null) return 1
				if (bv == null) return -1
				return (
					String(av).localeCompare(String(bv), undefined, {
						sensitivity: 'base',
					}) * dir
				)
			})
		}

		return res
	}
}

export const dashboardStore = new DashboardStore()
export { parseDateSafe, startOfDay, isSameDay, getDueDate }
